import type { FC, ReactNode } from "react";
import { Children, useCallback, useMemo, useState } from "react";

import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";
import { SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper/types";

import { LongArrowBackwardIcon } from "@jti/brand-icons/long-arrow-backward";
import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { useDirection } from "@jti/fe-common";

import { SwiperJti } from "../SwiperJti";

import styles from "./Carousel.module.scss";

const BREAKPOINT_SMALL_LARGE = 768; // css variable $breakpoint-small-large
const BREAKPOINT_LARGE = 1280; // css variable $breakpoint-large

export type CarouselProps = {
  desktopItemCount?: number;
  tabletItemCount?: number;
  children: ReactNode;
  isOverflowEnabled?: boolean;
  isControlHidden?: boolean;
  isViewportHeight?: boolean;
  isAutoHeight?: boolean;
  slidesPerView?: number;
  withoutSlidesMinHeight?: boolean;
  noBorderRadiusOnMobile?: boolean;
  showLoader?: boolean;
};

export const Carousel: FC<CarouselProps> = ({
  desktopItemCount = 3,
  tabletItemCount = 2,
  children,
  isOverflowEnabled,
  isControlHidden,
  isViewportHeight = false,
  isAutoHeight = false,
  slidesPerView,
  withoutSlidesMinHeight = false,
  noBorderRadiusOnMobile = false,
  showLoader = false,
}) => {
  const childrenArray = useMemo(() => Children.toArray(children), [children]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const direction = useDirection();
  const isRtl = direction === "rtl";
  const { breakpoint } = useDeviceBreakpoint();
  const { __ } = useTranslations();

  const currentItemsPerView = useMemo(() => {
    if (breakpoint === "desktop") return desktopItemCount;
    if (breakpoint === "tablet") return tabletItemCount;
    return 1;
  }, [breakpoint, desktopItemCount, tabletItemCount]);

  const shouldHideControls = childrenArray.length <= currentItemsPerView;

  const handleNextClick = useCallback(() => {
    if (!isEnd) {
      swiperInstance?.slideTo(activeIndex + 1);
    }
  }, [swiperInstance, activeIndex, isEnd]);

  const handlePrevClick = useCallback(() => {
    if (!isBeginning) {
      swiperInstance?.slideTo(activeIndex - 1);
    }
  }, [swiperInstance, activeIndex, isBeginning]);

  return (
    <div className={styles.carousel}>
      <SwiperJti
        showLoader={showLoader}
        autoHeight={isAutoHeight}
        rtl={isRtl}
        withoutSlidesMinHeight={withoutSlidesMinHeight}
        isOverflowEnabled={isOverflowEnabled}
        isShowNavigationArrows={false}
        isShowPaginationButtons={false}
        isContainerWidth={true}
        isViewportHeight={isViewportHeight}
        touchStartPreventDefault={true}
        key={`${tabletItemCount}-${desktopItemCount}`}
        slidesPerView={slidesPerView || 1}
        spaceBetween={24} // var(--des-dimension-gap-and-padding-general-away-medium, 24px)
        noBorderRadiusOnMobile={noBorderRadiusOnMobile}
        onSwiper={(swiper) => {
          setSwiperInstance(swiper);
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        }}
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.realIndex);
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        }}
        className={styles.content}
        breakpoints={{
          [BREAKPOINT_SMALL_LARGE]: {
            slidesPerView: tabletItemCount,
          },
          [BREAKPOINT_LARGE]: {
            slidesPerView: desktopItemCount,
          },
        }}
      >
        {childrenArray.map((child, index) => (
          <SwiperSlide key={index}>{child}</SwiperSlide>
        ))}
      </SwiperJti>

      {isControlHidden || childrenArray.length <= 1 ? null : (
        <div
          className={classNames(styles.controls, {
            [styles.controlsHidden]: shouldHideControls, // hide controls but keep space to avoid layout shifts, as advised by @piotr.cz
          })}
        >
          <button
            className={styles.arrowButton}
            data-testid="prev-button"
            onClick={handlePrevClick}
            disabled={isBeginning}
            aria-label={__("carousel.previous")}
          >
            <LongArrowBackwardIcon className={styles.arrowIcon} aria-hidden="true" />
          </button>
          <button
            className={classNames(styles.arrowButton)}
            data-testid="next-button"
            onClick={handleNextClick}
            disabled={isEnd}
            aria-label={__("carousel.next")}
          >
            <LongArrowForwardIcon className={styles.arrowIcon} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
};
